import { useEffect, useRef } from 'react';
import type { useSettlementPlacement } from './data/use-settlement-placement';
import { placementFootprint } from './data/settlement-placement';

export function SettlementPlacementControls({
  editor,
  label,
  offline,
}: {
  editor: ReturnType<typeof useSettlementPlacement>;
  label: string;
  offline: boolean;
}) {
  const focusRef = useRef<HTMLButtonElement>(null);
  const initiallyFocused = useRef(false);
  useEffect(() => {
    if (!editor.busy && !initiallyFocused.current && focusRef.current) {
      focusRef.current.focus();
      initiallyFocused.current = true;
    }
  }, [editor.busy]);
  const draft = editor.session?.draft;
  const building = editor.session?.layout.buildings.find((item) => item.id === draft?.buildingId);
  const blocked = editor.busy || offline;
  const footprint = building && draft ? placementFootprint(building, draft.rotation) : undefined;
  return (
    <section
      className="home-placement"
      aria-label={`Move ${label}`}
      data-testid="building-placement-editor"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !editor.busy) {
          event.preventDefault();
          editor.cancel();
        }
      }}
    >
      <header className="home-placement__heading">
        <strong>
          Move {label}
          {footprint && (
            <small className="home-placement__dimensions">
              {footprint.footprintWidth} × {footprint.footprintHeight} plot · {draft!.rotation * 90}
              °
            </small>
          )}
        </strong>
        <button type="button" onClick={editor.cancel} disabled={editor.busy} ref={focusRef}>
          Cancel
        </button>
      </header>
      {draft ? (
        <>
          <p className="home-placement__hint ar-visually-hidden">
            Drag to a clear spot. Pinch to zoom; arrows fine-tune. Rotate to change orientation.
          </p>
          <div className="home-placement__adjust">
            <label>
              <span className="ar-visually-hidden">District</span>
              <select
                aria-label="Placement district"
                disabled={blocked}
                value={draft.districtCode}
                onChange={(event) => editor.changeDistrict(event.target.value)}
              >
                {editor.session?.layout.districts
                  .filter((district) => district.unlocked_at)
                  .map((district) => (
                    <option key={district.code} value={district.code}>
                      {district.display_name}
                    </option>
                  ))}
              </select>
            </label>
            <div
              className="home-placement__steps"
              role="group"
              aria-label="Fine position adjustment"
              onKeyDown={(event) => {
                if (blocked) return;
                const offsets: Record<string, [number, number]> = {
                  ArrowLeft: [-1, 0],
                  ArrowRight: [1, 0],
                  ArrowUp: [0, -1],
                  ArrowDown: [0, 1],
                };
                const offset = offsets[event.key];
                if (offset) {
                  event.preventDefault();
                  editor.move(draft.x + offset[0], draft.y + offset[1]);
                }
              }}
            >
              {(
                [
                  [-1, 0, '↖', 'Move northwest'],
                  [0, -1, '↗', 'Move northeast'],
                  [0, 1, '↙', 'Move southwest'],
                  [1, 0, '↘', 'Move southeast'],
                ] as const
              ).map(([x, y, arrow, name]) => (
                <button
                  type="button"
                  key={name}
                  aria-label={name}
                  disabled={blocked}
                  onClick={() => editor.move(draft.x + x, draft.y + y)}
                >
                  {arrow}
                </button>
              ))}
              <button
                type="button"
                aria-label="Rotate building counterclockwise"
                title="Rotate counterclockwise"
                disabled={blocked}
                onClick={() => editor.rotate(-1)}
              >
                ↶
              </button>
              <button
                type="button"
                aria-label="Rotate building clockwise"
                title="Rotate clockwise"
                disabled={blocked}
                onClick={() => editor.rotate(1)}
              >
                ↷
              </button>
            </div>
          </div>
          <p role="status" className="home-placement__feedback" data-valid={!editor.problem}>
            {offline
              ? 'Reconnect to save. Your draft is still here.'
              : editor.busy
                ? 'Saving your position…'
                : (editor.problem ??
                  (editor.unchanged
                    ? 'Move or rotate. Changes are free.'
                    : 'Clear spot · ready to place'))}
            <span className="ar-visually-hidden">
              {' '}
              Plot {draft.x}, {draft.y}. Orientation {draft.rotation * 90} degrees.
            </span>
          </p>
          <button
            type="button"
            className="ar-button ar-button--primary home-placement__save"
            disabled={blocked || !!editor.error || !!editor.problem || editor.unchanged}
            onClick={() => void editor.save()}
          >
            {' '}
            {editor.busy ? 'Saving…' : 'Save position'}{' '}
          </button>
        </>
      ) : (
        <p role="status">
          {editor.busy ? 'Opening your layout…' : 'Your saved buildings are safe.'}
        </p>
      )}
      {editor.error && (
        <div className="home-placement__error" role="alert">
          <p>{editor.error}</p>
          <button type="button" disabled={blocked} onClick={() => void editor.refresh()}>
            Refresh layout
          </button>
        </div>
      )}
    </section>
  );
}
